import { useState, useMemo, useCallback } from 'react';
import { useGetPreQualConfigQuery } from '../../../../../../../utils/api_service/preQualApi';
import { useGetCCAConfigQuery } from '../../../../../../../utils/api_service/ccaApi';
import { JobPostingTabData } from '../../../../../../../utils/types/JobOpenings';
import {
  JobPostConfigStepId,
  IStepCompletionStatus,
  IUseJobPostConfigReturn,
} from '../interfaces';
import {
  isSettingsStepCompleted,
  isVideoStepCompleted,
  isPrequalStepCompleted,
  isCcaStepCompleted,
  calculateProgress,
} from '../functions';

interface UseJobPostConfigParams {
  jobPostingData?: JobPostingTabData;
  jobPostId?: string;
  hasCCALicense?: boolean;
  setActiveTab: (tab: string) => void;
}

export const useJobPostConfig = ({
  jobPostingData,
  jobPostId,
  hasCCALicense = false,
  setActiveTab,
}: UseJobPostConfigParams): IUseJobPostConfigReturn => {
  const [activeStep, setActiveStep] = useState<JobPostConfigStepId>('settings');

  // RTK Queries
  const { data: preQualConfig, isLoading: isPreQualLoading } =
    useGetPreQualConfigQuery(jobPostId || '', { skip: !jobPostId });

  const { data: ccaConfig, isLoading: isCcaLoading } = useGetCCAConfigQuery(
    jobPostId || '',
    { skip: !jobPostId },
  );

  const preQualQuestionsCount = preQualConfig?.questions?.length || 0;
  const ccaEnabled = ccaConfig?.enabled ?? false;
  const ccaQuestionsCount = ccaConfig?.questions?.length || 0;

  // Completion calculation
  const completionStatus: IStepCompletionStatus = useMemo(
    () => ({
      settings: isSettingsStepCompleted(jobPostingData),
      video: isVideoStepCompleted(jobPostingData),
      prequal: isPrequalStepCompleted(preQualQuestionsCount),
      cca: isCcaStepCompleted(ccaEnabled, ccaQuestionsCount),
    }),
    [jobPostingData, preQualQuestionsCount, ccaEnabled, ccaQuestionsCount],
  );

  const totalStepsCount = hasCCALicense ? 4 : 3;

  const completedStepsCount = useMemo(() => {
    let count = 0;
    if (completionStatus.settings) count++;
    if (completionStatus.video) count++;
    if (completionStatus.prequal) count++;
    if (hasCCALicense && completionStatus.cca) count++;
    return count;
  }, [completionStatus, hasCCALicense]);

  const progressPercent = useMemo(
    () => calculateProgress(completedStepsCount, totalStepsCount),
    [completedStepsCount, totalStepsCount],
  );

  // Custom step navigation proxy
  const makeStepSetActiveTab = useCallback(
    (step: JobPostConfigStepId) => {
      return (tab: string | ((prev: string) => string)) => {
        const tabName = typeof tab === 'function' ? tab('') : tab;

        if (step === 'settings') {
          if (tabName === 'jobPostDetails') {
            setActiveStep('video');
          }
        } else if (step === 'video') {
          if (tabName === 'preQualifyingQuestions') {
            setActiveStep('prequal');
          }
        } else if (step === 'prequal') {
          if (tabName === 'videoMessageDetails') {
            setActiveStep('video');
          } else if (tabName === 'cca') {
            setActiveStep('cca');
          } else if (tabName === 'preview') {
            setActiveTab('jobPostDetails');
          }
        } else if (step === 'cca') {
          if (tabName === 'preQualifyingQuestions') {
            setActiveStep('prequal');
          } else if (tabName === 'preview') {
            setActiveTab('jobPostDetails');
          }
        }
      };
    },
    [setActiveTab],
  );

  return {
    activeStep,
    setActiveStep,
    completionStatus,
    completedStepsCount,
    totalStepsCount,
    progressPercent,
    preQualQuestionsCount,
    isPreQualLoading,
    ccaEnabled,
    ccaQuestionsCount,
    isCcaLoading,
    makeStepSetActiveTab,
  };
};
